import { bnPageHead } from "@/lib/bn-head";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { BUSINESS_HOURS } from "@/content/business-hours";
import { InlineLeadForm } from "@/components/InlineLeadForm";

import { Mail, Phone, MapPin } from "lucide-react";

const URL = `${SITE.url}/bn/contact`;
const TITLE = "যোগাযোগ — ফ্রিল্যান্সার তামাল (রংপুর, বাংলাদেশ)";
const DESC =
  "এসইও, লোকাল এসইও বা AEO নিয়ে কথা বলতে চাইলে ইমেইল বা ফোনে যোগাযোগ করুন। প্রথম ২০ মিনিটের স্কোপিং কল বিনামূল্যে।";

const FAQ = [
  {
    q: "প্রথম কলে কী কী নিয়ে আলোচনা হয়?",
    a: "আপনার ব্যবসার লক্ষ্য, বর্তমান সাইটের অবস্থা, প্রতিযোগী কারা এবং বাজেট। ২০ মিনিটের এই কলটি বিনামূল্যে এবং এতে কোনো বাধ্যবাধকতা নেই।",
  },
  {
    q: "কত দ্রুত উত্তর পাব?",
    a: "কর্মদিবসে সাধারণত ২৪ ঘণ্টার মধ্যে ইমেইলের উত্তর দিই। জরুরি হলে হোয়াটসঅ্যাপে লিখুন।",
  },
  {
    q: "বাংলাদেশের বাইরের ক্লায়েন্ট নেন?",
    a: "হ্যাঁ। যুক্তরাষ্ট্র, যুক্তরাজ্য, অস্ট্রেলিয়া ও মধ্যপ্রাচ্যের ক্লায়েন্টের সঙ্গে নিয়মিত কাজ করি। যোগাযোগ ও রিপোর্টিং ইংরেজিতে হয়।",
  },
  {
    q: "ন্যূনতম চুক্তি কত মাসের?",
    a: "রিটেইনারের ক্ষেত্রে তিন মাস, কারণ এর কম সময়ে এসইও-র ফল মাপা যায় না। এককালীন অডিট বা কনসালটেশনে কোনো ন্যূনতম মেয়াদ নেই।",
  },
];

export const Route = createFileRoute("/bn/contact")({
  head: () =>
    bnPageHead({
      path: "/bn/contact",
      enPath: "/contact",
      title: TITLE,
      desc: DESC,
      keywords: ["seo expert contact", "যোগাযোগ", "এসইও পরামর্শ"],
      suffix: false,
    }),
  component: BnContact,
});

function BnContact() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${URL}/#contact-bn`,
    url: URL,
    inLanguage: "bn-BD",
    name: TITLE,
    description: DESC,
    mainEntity: {
      "@type": "Person",
      name: SITE.fullName,
      email: SITE.email,
      telephone: SITE.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.streetAddress,
        addressLocality: SITE.city,
        addressRegion: SITE.region,
        postalCode: SITE.postalCode,
        addressCountry: SITE.countryCode,
      },
    },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: "bn-BD",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <JsonLd data={schema} />
      <JsonLd data={faqSchema} />
      <Section className="pt-8 pb-16">
        <div className="mb-8 text-xs uppercase tracking-wider text-muted-foreground">
          <Link to="/contact" className="hover:text-foreground">EN</Link>
          <span className="mx-2">·</span>
          <span className="text-foreground">বাংলা</span>
        </div>
        <header className="max-w-3xl">
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">যোগাযোগ</h1>
          <p className="mt-6 text-lg text-muted-foreground">{DESC}</p>
        </header>

        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          <a
            href={`mailto:${SITE.email}`}
            className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
          >
            <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
            <div className="mt-3 text-sm text-muted-foreground">ইমেইল</div>
            <div className="mt-1 break-all font-medium">{SITE.email}</div>
          </a>
          <a
            href={`tel:${SITE.phone}`}
            className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
          >
            <Phone className="h-5 w-5 text-primary" aria-hidden="true" />
            <div className="mt-3 text-sm text-muted-foreground">ফোন ও হোয়াটসঅ্যাপ</div>
            <div className="mt-1 font-medium">{SITE.phone}</div>
          </a>
          <div className="rounded-2xl border border-border bg-card p-6">
            <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
            <div className="mt-3 text-sm text-muted-foreground">ঠিকানা</div>
            <div className="mt-1 text-sm">
              {SITE.streetAddress}, {SITE.city} {SITE.postalCode}, বাংলাদেশ
            </div>
          </div>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">{BUSINESS_HOURS.bn}</p>

        <div className="mt-10">
          <InlineLeadForm lang="bn" source="bn-contact-page" title="সরাসরি এখানেই লিখুন" showContactLink={false} />
        </div>
      </Section>

      <Section className="pb-24">
        <h2 className="font-display text-2xl mb-6">সাধারণ প্রশ্ন</h2>
        <dl className="grid gap-5 sm:grid-cols-2">
          {FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
              <dt className="font-display text-lg">{f.q}</dt>
              <dd className="mt-2 text-sm text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-10">
          <Link
            to="/bn/services"
            className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium"
          >
            সেবা ও খরচ দেখুন
          </Link>
        </div>
      </Section>
    </>
  );
}
